<!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->
<script setup>
import * as d3 from 'd3';
import { computed, onMounted, ref, toRefs, watch } from 'vue';

import { colormaps } from '@/src/components/analysis/colormaps';
import { useAnalysisStore } from '@/src/store/analysis';
import { useOCGCalculatorStore } from '@/src/store/calculators/OCGCalculatorStore';
import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';

import { useAppState } from '@/src/store/ocg-app-state';

import { applyGroupTransform } from './contour-svg-primitives';
import { coordinateTransformation } from './coordinateTransformation';
import { appendPixelGrid } from './mask-svg-primitives';

const props = defineProps({
  id: { type: String, required: true },
  showMasks: { type: Boolean, required: true },
  curImageID: { type: String, required: true },
  canvasWidth: { type: Number, required: true },
  canvasHeight: { type: Number, required: true },
});

// Getting stores:
const analysisStore = useAnalysisStore();
const viewportStateStore = useViewportStateStore();
const ocgCalculatorStore = useOCGCalculatorStore();
const appState = useAppState();

// Used to get current viewer:
const { id: viewID, curImageID, showMasks, canvasWidth, canvasHeight } = toRefs(props);

const isNormalized = computed(() => ocgCalculatorStore.isNormalized);

const overlay = ref(null);

// Coordinate transformation (shared with contours).
// Provides the image→screen matrix used by both masks and contours.
const { scaleFactor, overlayTransform, imageToScreenMatrix, curImageMetadata }
  = coordinateTransformation({
    currentImageUid: curImageID,
    id: viewID,
    canvasWidth,
    canvasHeight,
  });

const maskType = computed(() => {
  return viewportStateStore.getSelectedMaskByViewport(viewID.value);
});

const showColorbar = ref(true);
const showColormapContextMenu = ref(false);
const menuX = ref(0);
const menuY = ref(0);
const menuTriggerStyle = ref({});
const currentColormap = computed({
  get: () => viewportStateStore.getSelectedColormap(viewID.value),
  set: (colormap) => {
    viewportStateStore.setSelectedColormap(viewID.value, colormap);
  },
});

function getMask() {
  try {
    const biomarkerContainer = analysisStore.getBiomarkerContainer();
    const mask = biomarkerContainer.getMaskByImageUIDAndType(
      curImageID.value,
      maskType.value,
    );
    return mask;
  }
  catch (e) {
    return null;
  }
}

function getMaskRange() {
  try {
    const biomarkerContainer = analysisStore.getBiomarkerContainer();
    const range = biomarkerContainer.getBiomarkerNormalRange(maskType.value);
    if (!range) {
      return null;
    }
    return range;
  }
  catch (e) {
    return null;
  }
}

function getMaskData() {
  try {
    const mask = getMask();

    if (!mask) {
      return null;
    }

    const data = mask.getData(isNormalized.value);

    if (!data) {
      return null;
    }

    return data;
  }
  catch (e) {
    return null;
  }
}

// **********************************************************

const minValue = ref(0);
const maxValue = ref(0);

function updateMinMaxValues() {
  const rangeInfo = isNormalized.value ? getMaskRange() : null;
  if (rangeInfo) {
    [minValue.value, maxValue.value] = rangeInfo;
    return;
  }
  const range = getMask()?.getRange(isNormalized.value);
  if (range !== undefined) {
    const [min, max] = range;
    minValue.value = min;
    maxValue.value = max;
  }
  else {
    minValue.value = 0;
    maxValue.value = 1;
  }
}

// --- main render -----------------------------------------------------------

function createOverlay() {
  showColorbar.value = true;
  const root = d3.select(overlay.value);
  if (root.empty() || !root.node()) return;
  root.selectAll('svg').remove();

  if (!props.showMasks) return;

  const activeViewports = appState.activeViewers;
  if (!activeViewports.includes(viewID.value)) return;

  const data = getMaskData();
  if (!data || !data.values || data.values.length === 0) {
    showColorbar.value = false;
    return;
  };

  updateMinMaxValues();

  const svg = root
    .append('svg')
    .attr('width', '100%')
    .attr('height', '100%')
    .style('opacity', showMasks.value ? 1 : 0);

  // All mask geometry lives in this group in image-pixel coordinates; the
  // group carries the image→screen matrix via applyTransform.
  const group = svg.append('g').attr('class', 'mask-root');

  appendPixelGrid(
    group,
    data,
    minValue.value,
    maxValue.value,
    colormaps[currentColormap.value],
    1.0, // full opacity
  );

  applyTransform();
}

// Applies the image→screen affine to the mask group.
function applyTransform() {
  const group = d3.select(overlay.value).select('g.mask-root');
  applyGroupTransform(group, {
    overlayTransform: overlayTransform.value,
    scaleFactor: scaleFactor.value,
    imageToScreenMatrix: imageToScreenMatrix.value,
  });
}

// --- lifecycle + reactivity ------------------------------------------------

onMounted(() => {
  createOverlay();
});

// Camera pan/zoom/rotate: only re-apply the transform (cheap, no rebuild).
watch(
  () => overlayTransform.value,
  () => requestAnimationFrame(applyTransform),
);

watch(maskType, () => {
  createOverlay();
});

watch(
  () => curImageID.value,
  () => {
    createOverlay();
  },
  { immediate: true, deep: false },
);

// Handle late-arriving image metadata (image data loads asynchronously).
watch(
  () => curImageMetadata.value,
  (newVal) => {
    if (newVal && newVal.name !== '(none)') {
      createOverlay();
    }
  },
  { deep: true },
);

watch(
  () => showMasks.value,
  () => {
    if (showMasks.value) {
      createOverlay();
    }
    else {
      d3.select(overlay.value)
        .selectAll('svg')
        .transition()
        .duration(300)
        .style('opacity', 0);
    }
  },
  { immediate: true, deep: false },
);

// Colormap changes require a full rebuild.
watch(currentColormap, () => {
  createOverlay();
});

watch(isNormalized, () => {
  createOverlay();
});

// --- Colorbar UI functions -------------------------------------------------

const getColormapGradientStyle = () => {
  const gradientStops = Array.from({ length: 10 }, (_, i) => {
    const t = i / 9;
    return `${colormaps[currentColormap.value](1 - t)} ${t * 100}%`;
  }).join(', ');

  return {
    background: `linear-gradient(to bottom, ${gradientStops})`,
  };
};

const getColormapPreviewStyle = (name) => {
  const gradientStops = Array.from({ length: 10 }, (_, i) => {
    const t = i / 9;
    return `${colormaps[name](t)} ${t * 100}%`;
  }).join(', ');

  return {
    background: `linear-gradient(to right, ${gradientStops})`,
  };
};

const getColormapLabel = (name) => {
  const labels = {
    'plasma': 'Plasma',
    'viridis': 'Viridis',
    'turbo': 'Turbo',
    'magma': 'Magma',
    'inferno': 'Inferno',
    'cividis': 'Cividis',
    'red-yellow-blue': 'Red-Yellow-Blue',
    'red-blue': 'Red-Blue',
  };
  return labels[name] || name;
};

const showColormapMenu = (event) => {
  event.preventDefault();
  event.stopPropagation();

  // Set menu position
  menuX.value = event.clientX;
  menuY.value = event.clientY;

  // Position the invisible trigger element
  menuTriggerStyle.value = {
    position: 'fixed',
    left: `${event.clientX}px`,
    top: `${event.clientY}px`,
    width: '1px',
    height: '1px',
    pointerEvents: 'none',
    zIndex: -1,
  };

  showColormapContextMenu.value = true;
};

const selectColormap = (colormap) => {
  currentColormap.value = colormap;
  showColormapContextMenu.value = false;
};

// Add watcher for colormap changes
watch(
  () => currentColormap.value,
  () => {
    createOverlay(); // Recreate overlay with new colormap
  },
);
</script>

<script>
export default {
  name: 'UIDDisplayMasks',
};
</script>

<template>
  <div class="overlay">
    <!-- D3 SVG mount point -->
    <div
      ref="overlay"
      style="
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        will-change: transform;
      "
    />

    <!-- Floating Colorbar -->
    <div
      v-if="showColorbar"
      class="floating-colorbar"
      @contextmenu.prevent="showColormapMenu"
    >
      <div class="colorbar-gradient" :style="getColormapGradientStyle()" />
      <div class="colorbar-labels">
        <span class="colorbar-label max-label">{{ maxValue.toFixed(2) }}</span>
        <span class="colorbar-label min-label">{{ minValue.toFixed(2) }}</span>
      </div>
    </div>

    <!-- Invisible trigger element for Vuetify menu positioning -->
    <div ref="menuTrigger" class="menu-trigger" :style="menuTriggerStyle" />

    <!-- Vuetify Context Menu -->
    <v-menu
      v-model="showColormapContextMenu"
      :activator="$refs.menuTrigger"
      :target="[menuX, menuY]"
      absolute
      location="bottom"
      :persistent="false"
      close-on-content-click
    >
      <v-card class="colormap-menu-card">
        <v-card-title class="menu-title">
          Color Map
        </v-card-title>
        <v-list density="compact">
          <v-list-item
            v-for="(fn, name) in colormaps"
            :key="name"
            class="colormap-item"
            :class="{ active: currentColormap === name }"
            @click="selectColormap(name)"
          >
            <div class="">
              <div
                class="colormap-preview"
                :style="getColormapPreviewStyle(name)"
              />
              <h6>{{ getColormapLabel(name) }}</h6>
            </div>
          </v-list-item>
        </v-list>
      </v-card>
    </v-menu>
  </div>
</template>

<style scoped>
.overlay {
  position: absolute;
  pointer-events: none;
  z-index: 0;
  width: 100%;
  height: 100%;
}

.floating-colorbar {
  position: absolute;
  top: 40%;
  transform: translateY(-50%);
  left: 5px;
  width: 10px;
  height: 45%;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 4px;
  pointer-events: auto;
  z-index: 999999;
  cursor: context-menu;
  background: rgba(0, 0, 0, 0.1);
}

.colorbar-gradient {
  width: 100%;
  height: 100%;
  border-radius: 3px;
}

.colorbar-labels {
  position: absolute;
  left: 12px;
  top: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  pointer-events: none;
}

.colorbar-label {
  color: white;
  font-size: 10px;
  font-family: sans-serif;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
  white-space: nowrap;
}

.max-label {
  margin-top: -6px;
}

.min-label {
  margin-bottom: -6px;
}

.colorbar-title {
  position: absolute;
  left: -15px;
  top: 50%;
  transform: translateY(-50%) rotate(-90deg);
  color: white;
  font-size: 11px;
  font-family: sans-serif;
  font-weight: bold;
  white-space: nowrap;
  pointer-events: none;
  transform-origin: center;
}

.menu-trigger {
  position: absolute;
  pointer-events: none;
}

.colormap-menu-card {
  background: var(--md-surface, #2d2d2d) !important;
  border: 1px solid rgba(255, 255, 255, 0.2);
  min-width: 180px;
}

.colormap-menu-card .menu-title {
  font-size: 12px !important;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 8px 12px !important;
}

.colormap-item {
  transition: background-color 0.2s;
}

.colormap-item:hover {
  background: var(--md-surface-container, #404040) !important;
}

.colormap-item.active {
  background: var(--md-surface-container, #404040) !important;
  color: var(--md-primary, #bb86fc) !important;
}

.colormap-preview {
  width: 40px;
  height: 16px;
  border-radius: 2px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}
</style>
